'use client';

import dynamic from 'next/dynamic';

import { birdFootprints } from '@/config/bird-footprints';
import { BIRD_FOOTPRINT_STEPS } from '@/constants/birdFootprint';
import { useDeferredMount } from '@/hooks/useDeferredMount';

import styles from './BirdFootprints.module.css';
import {
  FootprintGlyph,
  trailGroupTransform,
  trailViewBox,
} from './birdFootprints.shared';

import type { BirdFootprintsMotionProps } from './BirdFootprintsMotion';
import type { BirdFootprintStep } from '@/constants/birdFootprint';

const BirdFootprintsMotionDynamic = dynamic(
  () => import('./BirdFootprintsMotion').then((mod) => mod.BirdFootprintsMotion),
  { ssr: false },
);

export type BirdFootprintsProps = Omit<BirdFootprintsMotionProps, 'staticFeet'>;

function BirdFootprintsStaticSvg({
  className,
  steps = BIRD_FOOTPRINT_STEPS,
  x = 0,
  y = 0,
  rotateDeg = 0,
  scale = 1,
  flipY = false,
  extraClassName,
}: {
  className?: string;
  steps?: readonly BirdFootprintStep[];
  x?: number;
  y?: number;
  rotateDeg?: number;
  scale?: number;
  flipY?: boolean;
  extraClassName?: string;
}) {
  const groupTransform = trailGroupTransform({ x, y, rotateDeg, scale, flipY });
  const viewBox = trailViewBox(rotateDeg);
  const feet = steps.map((print) => (
    <FootprintGlyph
      key={`${print.left}-${print.top}-${print.rotateDeg}`}
      print={print}
    />
  ));

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
      aria-hidden
      overflow="visible"
      className={[styles.root, className, extraClassName].filter(Boolean).join(' ')}
    >
      {groupTransform ? <g transform={groupTransform}>{feet}</g> : feet}
    </svg>
  );
}

/**
 * Decorative bird-footprint trail (Figma "Steps"). Motion stagger is deferred
 * until near the viewport or idle. A CSS `prefers-reduced-motion` twin paints
 * settled feet immediately so reduced-motion / visual-diff never see an empty
 * trail box.
 */
export function BirdFootprints({
  className,
  steps = BIRD_FOOTPRINT_STEPS,
  x = 0,
  y = 0,
  rotateDeg = 0,
  scale = 1,
  flipY = false,
  startDelay = 0,
  amount = birdFootprints.amount,
  readySelector,
  readyMinOpacity,
}: BirdFootprintsProps) {
  const { ref, ready } = useDeferredMount<HTMLSpanElement>();
  const viewBox = trailViewBox(rotateDeg);
  const slotClassName = [styles.root, className].filter(Boolean).join(' ');

  return (
    <span ref={ref} aria-hidden style={{ display: 'contents' }}>
      <BirdFootprintsStaticSvg
        className={className}
        steps={steps}
        x={x}
        y={y}
        rotateDeg={rotateDeg}
        scale={scale}
        flipY={flipY}
        extraClassName={styles.reducedStatic}
      />
      <span className={styles.motionSlot}>
        {ready ? (
          <BirdFootprintsMotionDynamic
            className={className}
            steps={steps}
            x={x}
            y={y}
            rotateDeg={rotateDeg}
            scale={scale}
            flipY={flipY}
            startDelay={startDelay}
            amount={amount}
            readySelector={readySelector}
            readyMinOpacity={readyMinOpacity}
          />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
            aria-hidden
            overflow="visible"
            className={slotClassName}
          />
        )}
      </span>
    </span>
  );
}
